import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Sparkles, X, Eye, EyeOff, Bot, User, ExternalLink } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

import { Editor } from '@tinymce/tinymce-react';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/code';
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/image';

const API = process.env.REACT_APP_API_URL;

export default function PaperAdminBlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [works, setWorks] = useState([]);
  const [selectedWorkId, setSelectedWorkId] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '', isPublished: true });
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBlogs = () => {
    setLoading(true);
    axios.get(`${API}/api/paperBlog`, {
      params: { page: 1, limit: 100, all: true },
      headers,
    })
      .then(res => {
        setBlogs(res.data.blogs || res.data.rows || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchWorks = () => {
    axios.get(`${API}/api/paperWork`, { params: { limit: 200, page: 1 } })
      .then(res => setWorks(res.data.works || []))
      .catch(() => {});
  };

  useEffect(() => { fetchBlogs(); fetchWorks(); }, []);

  const handleGenerate = async () => {
    if (!selectedWorkId) { alert('행사를 선택해주세요.'); return; }
    const work = works.find(w => w.id === parseInt(selectedWorkId));
    if (!window.confirm(`"${work?.title}" 비하인드 스토리를 생성합니다. 진행하시겠습니까?`)) return;
    setGenerating(true);
    try {
      await axios.get(`${API}/api/paperBlog/generate`, { params: { workId: selectedWorkId }, headers });
      setSelectedWorkId('');
      fetchBlogs();
    } catch (err) {
      alert(err.response?.data?.message || '스토리 생성 중 오류가 발생했습니다.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`"${title}" 스토리를 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API}/api/paperBlog/${id}`, { headers });
      fetchBlogs();
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const openEdit = (blog) => {
    setEditingBlog(blog);
    setEditForm({
      title: blog.title || '',
      content: blog.content || '',
      isPublished: blog.isPublished !== false,
    });
  };

  const handleSave = async () => {
    if (!editingBlog) return;
    setSaving(true);
    try {
      const content = editorRef.current ? editorRef.current.getContent() : editForm.content;
      const plainText = content.replace(/<[^>]*>/g, '');
      const summary = plainText.substring(0, 100);
      await axios.put(`${API}/api/paperBlog/${editingBlog.id}`, {
        title: editForm.title,
        content,
        summary,
        isPublished: editForm.isPublished,
      }, { headers });
      setEditingBlog(null);
      fetchBlogs();
    } catch {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Admin Nav */}
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
        <Link to={`${PAPER_BASE}/admin/members`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">회원 관리</Link>
        <Link to={`${PAPER_BASE}/admin/works`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">행사 관리</Link>
        <Link to={`${PAPER_BASE}/admin/services`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">서비스 관리</Link>
        <Link to={`${PAPER_BASE}/admin/partners`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">파트너 관리</Link>
        <Link to={`${PAPER_BASE}/admin/blogs`} className="text-sm font-medium text-violet-400 border-b-2 border-violet-400 pb-1 whitespace-nowrap">스토리 관리</Link>
      </div>

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-white tracking-wide">스토리 관리</h1>
        <div className="flex items-center gap-2">
          <select
            value={selectedWorkId}
            onChange={e => setSelectedWorkId(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-violet-400/50 max-w-[200px]"
          >
            <option value="">행사 선택</option>
            {works.map(w => (
              <option key={w.id} value={w.id}>
                {w.title} ({w.category === 'FESTIVAL' ? '축제' : '공연'})
              </option>
            ))}
          </select>
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedWorkId}
            className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            {generating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                생성 중...
              </>
            ) : (
              <>
                <Sparkles size={16} /> 생성
              </>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin mx-auto" />
        </div>
      ) : blogs.length === 0 ? (
        <div className="text-center py-20 text-white/30">등록된 스토리가 없습니다.</div>
      ) : (
        <div className="space-y-3">
          {blogs.map(blog => (
            <div key={blog.id} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <button
                      onClick={() => openEdit(blog)}
                      className="text-white/90 text-sm font-medium hover:text-violet-400 transition-colors text-left truncate"
                    >
                      {blog.title}
                    </button>
                    {blog.isPublished ? (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-500/15 text-green-400">
                        <Eye size={10} /> 공개
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white/10 text-white/40">
                        <EyeOff size={10} /> 비공개
                      </span>
                    )}
                    {blog.autoGenerated ? (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-violet-500/15 text-violet-400">
                        <Bot size={10} /> AI
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-500/15 text-blue-400">
                        <User size={10} /> 수동
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-white/30">
                    {blog.work && (
                      <span className="flex items-center gap-1">
                        <ExternalLink size={10} />
                        {blog.work.title}
                      </span>
                    )}
                    <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(blog)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-violet-400 hover:bg-violet-400/10 transition-all text-xs"
                  >
                    편집
                  </button>
                  <button
                    onClick={() => handleDelete(blog.id, blog.title)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-red-400 hover:bg-red-400/10 transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingBlog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setEditingBlog(null)}>
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#1a1428] border border-white/10 rounded-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-white">스토리 편집</h2>
              <button onClick={() => setEditingBlog(null)} className="text-white/30 hover:text-white/60 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-white/40 mb-1.5">제목</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs text-white/40 mb-1.5">내용</label>
                <Editor
                  onInit={(evt, editor) => { editorRef.current = editor; }}
                  initialValue={editForm.content}
                  init={{
                    height: 400,
                    menubar: false,
                    base_url: '/tinymce',
                    skin: 'oxide-dark',
                    content_css: 'dark',
                    toolbar: 'undo redo | formatselect fontsize | bold italic underline forecolor | alignleft aligncenter alignright | bullist numlist | image link | code',
                    plugins: ['code', 'table', 'lists', 'link', 'autolink', 'image'],
                    fontsize_formats: '10pt 12pt 14pt 18pt 24pt',
                    content_style: 'body { font-family:Pretendard,sans-serif; font-size:12pt; color: #e2e8f0; background: #1a1428; } img { max-width: 100%; height: auto; border-radius: 8px; margin: 12px 0; }',
                  }}
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs text-white/40">공개 여부</label>
                <button
                  onClick={() => setEditForm(prev => ({ ...prev, isPublished: !prev.isPublished }))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${editForm.isPublished ? 'bg-violet-500' : 'bg-white/10'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${editForm.isPublished ? 'left-5' : 'left-0.5'}`} />
                </button>
                <span className="text-xs text-white/60">{editForm.isPublished ? '공개' : '비공개'}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
              <button
                onClick={() => setEditingBlog(null)}
                className="px-5 py-2 text-sm text-white/40 hover:text-white/60 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
